import { formatDate } from '@if/core/utils'
import { useComputeMechanicalWarrantyLoan } from '@src/data/api/calculator-api/calculator-api'
import { ComputeMechanicalWarrantyRequestDto } from '@src/types/ComputeMechanicalWarrantyRequestDto'
import { ComputeMechanicalWarrantyResultDto } from '@src/types/ComputeMechanicalWarrantyResultDto'
import { FinancingProgramProvider } from '@src/types/FinancingProgramProvider'
import { MechanicalWarrantyWorksheet } from '@src/types/WorksheetSchema'
import { useMemo } from 'react'

export const mapMechanicalWarrantyWorksheetToComputeDto = (
  worksheet: MechanicalWarrantyWorksheet,
  creditApplicationId: string,
  provider: FinancingProgramProvider,
): ComputeMechanicalWarrantyRequestDto => {
  return {
    creditApplicationId,
    providerId: provider.id,
    paymentFrequency: worksheet.paymentFrequency,
    term: worksheet.term,
    deliveryOn: formatDate(worksheet.deliveryOn),
    firstPaymentDate: formatDate(worksheet.firstPaymentOn),
    options: worksheet.additions?.options || [],
    surcharges: worksheet.additions?.surcharges || [],
    mechanicalWarrantyProductId: worksheet.mechanicalWarrantyProductId,
  }
}

interface UseMechanicalWarrantyComputeParams {
  creditApplicationId: string
  worksheet: MechanicalWarrantyWorksheet | null
  merchantMechanicalWarrantyProvider: FinancingProgramProvider | null
}

interface UseMechanicalWarrantyComputeReturn {
  computeDto: ComputeMechanicalWarrantyRequestDto | null
  computedResult: ComputeMechanicalWarrantyResultDto | null
  isComputing: boolean
}

const isValidForComputation = (
  worksheet: MechanicalWarrantyWorksheet | null,
  provider: FinancingProgramProvider | null,
): worksheet is MechanicalWarrantyWorksheet => {
  return !!(
    worksheet?.paymentFrequency &&
    worksheet.term &&
    worksheet.firstPaymentOn &&
    worksheet.deliveryOn &&
    provider &&
    worksheet.mechanicalWarrantyProductId
  )
}

export const useMechanicalWarrantyCompute = ({
  creditApplicationId,
  worksheet,
  merchantMechanicalWarrantyProvider,
}: UseMechanicalWarrantyComputeParams): UseMechanicalWarrantyComputeReturn => {
  const computeDto = useMemo((): ComputeMechanicalWarrantyRequestDto | null => {
    if (!isValidForComputation(worksheet, merchantMechanicalWarrantyProvider)) {
      return null
    }

    return mapMechanicalWarrantyWorksheetToComputeDto(
      worksheet,
      creditApplicationId,
      merchantMechanicalWarrantyProvider!,
    )
  }, [creditApplicationId, merchantMechanicalWarrantyProvider, worksheet])

  const [computedResult, isComputing] = useComputeMechanicalWarrantyLoan(computeDto ?? undefined)

  return {
    computeDto,
    computedResult,
    isComputing,
  }
}
